import { Navigate, Outlet, useLocation } from '@tanstack/react-router'

import { useUserAccountContext } from '@/api/v1/auth/get-user-account-context'
import { useUserAccountContexts } from '@/api/v1/auth/get-user-account-contexts'
import { ContextualUpsellProvider } from '@/components/common/contextual-upsell'
import { Enforce2FaProvider } from '@/components/common/enforce-2fa'
import { FeaturePaywallProvider } from '@/components/common/feature-paywall'
import { MobileBannersBar } from '@/components/common/mobile-banners-bar'
import { PlanFinderDialogProvider } from '@/components/common/plan-finder'
import { PlanPaywallProvider } from '@/components/common/plan-paywall'
import { SandboxDialogProvider } from '@/components/common/sandbox'
import { SidebarInset, SidebarProvider } from '@/components/sui/sidebar'
import { useIsGleapVisible } from '@/hooks/use-is-gleap-visible'
import { shouldRenderGleapSpacer } from '@/integrations/gleap'
import { UserHubProvider } from '@/integrations/signalr'
import { useContextStore } from '@/stores/context'

import { AppSidebar } from './components/app-sidebar'

export default function AuthenticatedLayout() {
  // These hooks will automatically refetch:
  // - When the tab returns to focus (refetchOnWindowFocus: true)
  // - Every 5 minutes (refetchInterval: 5 * 60 * 1000)
  // - useUserAccountContexts also validates if the user still has access
  useUserAccountContexts()
  useUserAccountContext()

  const isGleapVisible = useIsGleapVisible()
  const { pathname } = useLocation()
  const showSpacer = isGleapVisible && shouldRenderGleapSpacer(pathname)

  // The onboarding setup modal only lives on the dashboard. A provisional
  // account (setup not completed) must not use any other page, so every
  // deep link is sent back to the dashboard until the profile step is done.
  const requiresAccountSetup = useContextStore(
    (s) => s.userAccountContext?.requiresAccountSetup ?? false,
  )
  if (requiresAccountSetup && pathname !== '/') {
    return <Navigate to="/" replace />
  }

  return (
    <UserHubProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="max-h-svh overflow-auto">
          <MobileBannersBar />
          <Outlet />
          {showSpacer && <div aria-hidden className="h-20 shrink-0" />}
        </SidebarInset>
        <FeaturePaywallProvider />
        <PlanPaywallProvider />
        <PlanFinderDialogProvider />
        <SandboxDialogProvider />
        <ContextualUpsellProvider />
        <Enforce2FaProvider />
      </SidebarProvider>
    </UserHubProvider>
  )
}
